import { AlertTriangle, BatteryLow, Clock, MapPinOff } from "lucide-react";
import type * as React from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/v2/ui/primitives/tooltip";

export type WarningSeverity = "low" | "medium" | "high";

export interface FieldWarningProps {
  children: React.ReactNode;
  warning?: {
    message: string;
    severity: WarningSeverity;
    type?: "stale" | "battery" | "location" | "generic";
  };
  showIcon?: boolean;
  className?: string;
}

const severityColors = {
  low: "tw-text-warning",
  medium: "tw-text-warning",
  high: "tw-text-destructive",
};

const warningIcons = {
  stale: Clock,
  battery: BatteryLow,
  location: MapPinOff,
  generic: AlertTriangle,
};

export function FieldWarning({
  children,
  warning,
  showIcon = true,
  className,
}: FieldWarningProps) {
  if (!warning) {
    return <>{children}</>;
  }

  const Icon = warningIcons[warning.type || "generic"];
  const colorClass = severityColors[warning.severity];
  const content = showIcon ? (
    <>
      <Icon className={cn("tw-h-4 tw-w-4 tw-flex-shrink-0", colorClass)} />
      {children}
    </>
  ) : (
    <>{children}</>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild={true}>
          <span
            className={cn(
              "tw-inline-flex tw-items-center tw-gap-1.5",
              className
            )}
          >
            {content}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{warning.message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
